import { Box, MenuItem, Select, Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import { deleteCategoryReq } from "@domain/changelog"
import { showErrorToast, showSuccessToast } from "@utils/toast"

export default function DeleteCategoryDialog() {
  const {
    translate,
    isDeleteCategoryOpen,
    deleteCategoryTarget,
    categories,
    closeDeleteCategory,
    refetchCategories,
    refetchAnnouncements
  } = useChangelogPageContext()

  const [reassignTo, setReassignTo] = useState("")
  const [loading, setLoading] = useState(false)

  const otherCategories = useMemo(
    () => categories.filter((c) => c._id !== deleteCategoryTarget?._id),
    [categories, deleteCategoryTarget]
  )

  useEffect(() => {
    if (isDeleteCategoryOpen) {
      setReassignTo(otherCategories[0]?._id || "")
    }
  }, [isDeleteCategoryOpen, otherCategories])

  const targetName =
    deleteCategoryTarget?.translations?.tr?.name ||
    Object.values(deleteCategoryTarget?.translations || {})[0]?.name ||
    ""

  const onDelete = async () => {
    if (!deleteCategoryTarget) {
      return
    }
    if (!reassignTo) {
      showErrorToast(translate("changelog_pick_reassign"))
      return
    }
    setLoading(true)
    try {
      const res = await deleteCategoryReq({
        _id: deleteCategoryTarget._id,
        reassignToCategoryId: reassignTo
      })
      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || translate("changelog_save_failed"))
        return
      }
      showSuccessToast(translate("changelog_category_deleted"))
      refetchCategories()
      refetchAnnouncements()
      closeDeleteCategory()
    } finally {
      setLoading(false)
    }
  }

  return (
    <DialogProvider
      title={translate("changelog_delete_category")}
      isOpen={isDeleteCategoryOpen}
      setClose={closeDeleteCategory}
      size="small"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: closeDeleteCategory
      }}
      rightButton={{
        text: translate("delete"),
        color: "error",
        onClick: onDelete,
        loading,
        disabled: !reassignTo
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "0.9rem", mb: 2 }}>
          {translate("changelog_delete_category_desc").replace(
            "{name}",
            targetName
          )}
        </Typography>
        <Stack gap={0.75}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
            {translate("changelog_reassign_to")}
          </Typography>
          {otherCategories.length === 0 ? (
            <Typography sx={{ fontSize: "0.85rem", color: "error.main" }}>
              {translate("changelog_no_other_category")}
            </Typography>
          ) : (
            <Select
              size="small"
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              fullWidth
            >
              {otherCategories.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.translations?.tr?.name ||
                    Object.values(c.translations || {})[0]?.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </Stack>
      </Box>
    </DialogProvider>
  )
}
